import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import authService from '../services/AuthService';
import ErrorHandler from '../utils/ErrorHandler';

/**
 * Authentication Store
 * Manages authentication state, login/logout, and session persistence
 */
export const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      isAuthenticated: false,
      user: null,
      isLoading: false,
      error: null,
      isInitialized: false,

      // Actions
      login: async (credentials) => {
        set({ isLoading: true, error: null });
        
        try {
          const result = await authService.login(credentials);
          
          if (result.success) {
            set({
              isAuthenticated: true,
              user: result.user,
              isLoading: false,
              error: null,
              isInitialized: true
            });
            return result;
          } else {
            throw new Error(result.message || 'Login failed');
          }
        } catch (error) {
          const errorInfo = ErrorHandler.handleApiError(error, 'Login');
          set({
            isAuthenticated: false,
            user: null,
            isLoading: false,
            error: errorInfo.message
          });
          throw error;
        }
      },

      logout: () => {
        authService.logout();
        set({
          isAuthenticated: false,
          user: null,
          error: null,
          isInitialized: false // Reset initialization flag to allow re-login
        });
      },

      // Handle 401 errors - force logout and clear all data
      handle401Error: () => {
        console.log('[AuthStore] Handling 401 error - forcing logout');
        authService.logout();
        set({
          isAuthenticated: false,
          user: null,
          error: 'Your session has expired. Please log in again.',
          isInitialized: true, // Keep initialized to prevent re-init loops
          isLoading: false
        });
        
        // Force a page reload to ensure clean state
        setTimeout(() => {
          if (typeof window !== 'undefined') {
            window.location.reload();
          }
        }, 1000);
      },

      initialize: async () => {
        if (get().isInitialized) return get().isAuthenticated;
        
        set({ isLoading: true });
        
        try {
          const isLoggedIn = await authService.initialize();
          const user = authService.getCurrentUser();
          
          set({
            isAuthenticated: isLoggedIn,
            user: user,
            isLoading: false,
            isInitialized: true,
            error: null
          });
          
          return isLoggedIn;
        } catch (error) {
          console.error('Auth initialization error:', error);
          set({
            isAuthenticated: false,
            user: null,
            isLoading: false,
            isInitialized: true,
            error: null
          });
          return false;
        }
      },

      clearError: () => set({ error: null }),

      // Session validation
      validateSession: async () => {
        try {
          const isValid = await authService.validateSession();
          if (!isValid) {
            get().logout();
          }
          return isValid;
        } catch (error) {
          console.error('Session validation error:', error);
          get().logout();
          return false;
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user
      })
    }
  )
);